import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { authHeaders } from '../../../auth/clerk';
import { API_BASE_URL } from '../../../constants';

/**
 * The answer's session, rendered in the panel (#1507, flag on): the same account-scoped, disclosed read the Debug UI
 * makes (`GET /api/debug/trace/{session}`), as a timeline with time, cost and event count. Nothing new is asked of the
 * server; the Debug UI link stays beside it for the full view.
 */

export function costOf(e: any): number {
  const u = e?.type === 'mission_started' ? e.planner_usage : e?.usage;
  return u && typeof u.cost_usd === 'number' ? u.cost_usd : 0;
}

/** The session's cost as the Debug UI totals it: every usage block, or mission_complete's total when that is larger. */
export function totalCost(events: any[]): number {
  const sum = events.reduce((n, e) => n + costOf(e), 0);
  const mc = events.find((e) => e?.type === 'mission_complete');
  return mc && typeof mc.total_cost_usd === 'number' && mc.total_cost_usd > sum ? mc.total_cost_usd : sum;
}

/** One event in words, after the Debug UI's own summaries. */
export function summarizeEvent(e: any): string {
  switch (e?.type) {
    case 'mission_started': return `${e.sub_task_count ?? '?'} sub-tasks`;
    case 'sub_task_started': return `${e.sub_task_id || ''} · ${e.worker_type || ''}${e.attempt > 1 ? ` (retry ${e.attempt})` : ''}${e.description ? ` · ${e.description}` : ''}`;
    case 'worker_result': return `${e.sub_task_id || ''} → ${e.status} · ${e.row_count ?? '?'} rows${e.execution_time_ms != null ? ` · ${e.execution_time_ms}ms` : ''}${e.error ? ` · ${String(e.error).slice(0, 100)}` : ''}`;
    case 'gatekeeper_verdict': return typeof e.verdict === 'object' ? String(e.verdict?.verdict || '') : `${e.sub_task_id || ''} → ${e.verdict}${e.confidence != null ? ` (${e.confidence})` : ''}`;
    case 'retry_queued': return `${e.sub_task_id || ''} → attempt ${e.next_attempt}`;
    case 'retry_exhausted': return `${e.sub_task_id || ''} · attempts=${e.attempts}`;
    case 'formatter_result': return `${e.sub_task_id || ''} → ${e.status} · ${(e.bundles || []).length} bundle(s)`;
    case 'synthesis_started': return `${e.data_producing_tasks ?? '?'} data-producing tasks`;
    case 'synthesis_result': return `${(e.synthesis?.key_numbers || []).length} key numbers · ${(e.synthesis?.follow_ups || []).length} follow-ups`;
    case 'route_decided': return `route=${e.route || ''}${typeof e.confidence === 'number' ? ` (${e.confidence})` : ''}${e.decided_by ? ` · ${e.decided_by}` : ''}`;
    case 'agent_request': return `${e.caller || ''} → ${e.callee || ''} · ${e.request_type || ''}`;
    case 'agent_result': return `${e.callee || ''} → ${e.caller || ''} · ${e.status || ''}${e.reason ? ` · ${e.reason}` : ''}`;
    case 'sub_task_skipped': return `${e.sub_task_id || ''} · ${e.reason || ''}`;
    case 'sub_task_error': return `${e.sub_task_id || ''}: ${e.error || ''}`;
    case 'synthesis_error':
    case 'mission_error': return String(e.error || '');
    case 'mission_complete': return e.awaiting_user ? 'awaiting user' : e.mission_status ? `status=${e.mission_status}` : '';
    default: return '';
  }
}

function tone(e: any): string {
  const t = e?.type;
  if (['retry_exhausted', 'sub_task_error', 'synthesis_error', 'mission_error'].includes(t) || (t === 'agent_result' && e.status && e.status !== 'ok')) return 'var(--bad)';
  if (['retry_queued', 'clarification_required', 'mission_cancelled'].includes(t)) return 'var(--warn)';
  if (['worker_result', 'gatekeeper_verdict', 'formatter_result', 'synthesis_result', 'mission_complete', 'agent_result'].includes(t)) return 'var(--ok)';
  return 'var(--ink-4, var(--ink-3))';
}

const secs = (s: number) => (s < 60 ? `${s.toFixed(1)}s` : `${(s / 60).toFixed(1)}m`);

type Load = { state: 'loading' } | { state: 'failed'; status?: number } | { state: 'done'; events: any[] };

export default function SessionTrace({ sessionId }: { sessionId: string }) {
  const [load, setLoad] = React.useState<Load>({ state: 'loading' });
  React.useEffect(() => {
    const ctrl = new AbortController();
    setLoad({ state: 'loading' });
    (async () => {
      try {
        const base = API_BASE_URL || process.env.REACT_APP_API_BASE || '';
        const resp = await fetch(`${base}/api/debug/trace/${encodeURIComponent(sessionId)}`, { headers: { ...(await authHeaders()) }, signal: ctrl.signal });
        if (!resp.ok) { setLoad({ state: 'failed', status: resp.status }); return; }
        const body = await resp.json();
        setLoad({ state: 'done', events: Array.isArray(body?.events) ? body.events : [] });
      } catch {
        if (!ctrl.signal.aborted) setLoad({ state: 'failed' });
      }
    })();
    return () => ctrl.abort();
  }, [sessionId]);

  if (load.state === 'loading') {
    return <Box data-testid="session-trace-loading" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'var(--ink-3)', fontSize: 13 }}><CircularProgress size={14} />Loading the trace…</Box>;
  }
  if (load.state === 'failed') {
    return (
      <Typography data-testid="session-trace-failed" variant="body2" color="text.secondary">
        {load.status === 404 ? 'This trace is not available to you.' : 'Could not load the trace.'} Open the full trace opens it in the trace viewer.
      </Typography>
    );
  }
  const events = load.events;
  const t0 = typeof events[0]?.ts === 'number' ? events[0].ts : null;
  const last = typeof events[events.length - 1]?.ts === 'number' ? events[events.length - 1].ts : null;
  const cost = totalCost(events);
  return (
    <Box data-testid="session-trace" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', border: '1px solid var(--line)', borderRadius: '10px', overflow: 'hidden' }}>
        {[['Time', t0 != null && last != null ? secs(last - t0) : '—'], ['Cost', `$${cost.toFixed(cost < 0.1 ? 3 : 2)}`], ['Events', String(events.length)]].map(([k, v], i) => (
          <Box key={k} sx={{ px: 1.75, py: 1.25, borderRight: i < 2 ? '1px solid var(--line-soft, var(--line))' : 0 }}>
            <Box component="span" sx={{ display: 'block', fontSize: 11.5, color: 'var(--ink-3)' }}>{k}</Box>
            <Box component="b" data-testid={`session-trace-${k.toLowerCase()}`} sx={{ fontWeight: 500, fontSize: 16, fontVariantNumeric: 'tabular-nums' }}>{v}</Box>
          </Box>
        ))}
      </Box>
      {events.length === 0
        ? <Typography variant="body2" color="text.secondary">This session recorded no events.</Typography>
        : (
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column' }}>
            {events.map((e, i) => {
              const c = costOf(e);
              const summary = summarizeEvent(e);
              return (
                <Box component="li" key={i} data-testid="session-trace-event" data-type={e?.type}
                  sx={{ display: 'grid', gridTemplateColumns: '56px 12px minmax(0,1fr) auto', gap: 1.25, py: 0.75, fontSize: 12.5, alignItems: 'start' }}>
                  <Box component="time" sx={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)', lineHeight: 1.7 }}>
                    {t0 != null && typeof e?.ts === 'number' ? `+${secs(e.ts - t0)}` : ''}
                  </Box>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone(e), mt: '6px', justifySelf: 'center' }} />
                  <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    <Box component="span" sx={{ fontWeight: 500 }}>{String(e?.type || 'event').replace(/_/g, ' ')}</Box>
                    {summary && <Box component="span" sx={{ color: 'var(--ink-2)' }}>{' · '}{summary}</Box>}
                  </Box>
                  <Box sx={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-3)' }}>{c > 0 ? `$${c.toFixed(4)}` : ''}</Box>
                </Box>
              );
            })}
          </Box>
        )}
    </Box>
  );
}
